import type { TableKey } from './en';

/**
 * Russian UI strings. Typed against the English key set, so drift is a
 * compile error.
 *
 * Russian needs three plural stems (one / few / many); `#other` is required by
 * the type but only reached for fractional counts.
 */
export const ru: Record<TableKey, string> = {
  'app.name': 'Мяу-трекер',
  'app.tagline': 'Трекер наград Fallout 76',

  'meow.label': 'Мяу',
  'meow.bubble': 'Мяу!',
  'meow.aria': 'Отметить «{name}» как полученное',
  'meow.all': 'Мяу всем',
  'meow.none': 'Снять все мяу',

  'filter.search': 'Поиск наград…',
  'filter.searchAria': 'Поиск наград по названию, источнику или заметке',
  'filter.all': 'Все',
  'filter.done': 'Промяукано',
  'filter.todo': 'Осталось',
  'filter.rarity': 'Редкость',
  'filter.type': 'Тип',
  'filter.reset': 'Сбросить фильтры',
  'filter.empty': 'По этим фильтрам ничего нет.',
  'filter.emptyHint': 'Очистите поиск или вернитесь к «Все».',

  'stats.collected': '{done} / {total}',
  'stats.percent': '{percent}%',
  'stats.remaining#one': 'осталась {n}',
  'stats.remaining#few': 'осталось {n}',
  'stats.remaining#many': 'осталось {n}',
  'stats.remaining#other': 'осталось {n}',
  'stats.allDone': 'Всё собрано — отлично!',
  'stats.progressAria': 'Собрано {done} из {total} наград',

  'group.collapse': 'Свернуть раздел',
  'group.expand': 'Развернуть раздел',
  'group.jump': 'Перейти к разделу',

  'phase.live': 'Идёт сейчас',
  'phase.daysLeft#one': 'остался {n} день',
  'phase.daysLeft#few': 'осталось {n} дня',
  'phase.daysLeft#many': 'осталось {n} дней',
  'phase.daysLeft#other': 'осталось {n} дня',
  'phase.lastDay': 'Последний день',
  'phase.startsIn#one': 'Начнётся через {n} день',
  'phase.startsIn#few': 'Начнётся через {n} дня',
  'phase.startsIn#many': 'Начнётся через {n} дней',
  'phase.startsIn#other': 'Начнётся через {n} дня',
  'phase.startsTomorrow': 'Начнётся завтра',
  'phase.ended': 'Завершено',

  'now.live': 'Сейчас идёт',
  'now.next': 'Дальше',
  'now.before': 'Событие начнётся {date}',
  'now.after': 'Событие завершилось.',

  'odds.label': 'Шансы',
  'odds.expected#one': 'в среднем ≈{value} попытка',
  'odds.expected#few': 'в среднем ≈{value} попытки',
  'odds.expected#many': 'в среднем ≈{value} попыток',
  'odds.expected#other': 'в среднем ≈{value} попытки',
  'odds.ninety': '90% — в пределах {value}',
  'odds.explain':
    'Для ещё не полученных предметов, считая один бросок добычи за попытку с указанными шансами. Гарантированные награды и неизвестные шансы не учитываются.',

  'undo.meowed': 'Промяукано: {n}',
  'undo.unmeowed': 'Снято мяу: {n}',
  'undo.action': 'Отменить',

  'share.title': 'Поделиться картинкой',
  'share.modeAll': 'Всё',
  'share.modeDone': 'Только собранное',
  'share.share': 'Поделиться…',
  'share.copy': 'Скопировать',
  'share.copied': 'Картинка скопирована — вставьте её в чат.',
  'share.copyFailed': 'Не удалось скопировать картинку.',
  'share.download': 'Скачать PNG',
  'share.rendering': 'Готовим картинку…',
  'share.failed': 'Не удалось создать картинку.',
  'share.hint': 'На телефоне удерживайте картинку, чтобы сохранить её.',
  'share.imageAlt': 'Предпросмотр картинки с прогрессом',
  'share.nothingYet': 'Пока ничего не собрано.',
  'share.variantList': 'Список',
  'share.variantMap': 'Карта',

  'view.list': 'Список',
  'view.map': 'Карта',

  'map.zoomIn': 'Приблизить',
  'map.zoomOut': 'Отдалить',
  'map.fit': 'Показать всю карту',
  'map.loading': 'Загружаем карту…',
  'map.pinAria': '{name}: {done} из {total}',
  'map.placeAll': 'Мяу всем здесь',
  'map.placeNone': 'Снять все мяу здесь',
  'map.placesCleared': 'Зачищено мест: {done} / {total}',

  'profile.default': 'Основной',
  'profile.label': 'Профиль',
  'profile.new': 'Новый профиль',
  'profile.rename': 'Переименовать',
  'profile.duplicate': 'Дублировать',
  'profile.delete': 'Удалить',
  'profile.namePlaceholder': 'Имя персонажа',
  'profile.deleteConfirm': 'Удалить «{name}» и весь его прогресс? Это необратимо.',
  'profile.deleteLast': 'Нельзя удалить единственный профиль.',
  'profile.copySuffix': '{name} (копия)',

  'settings.title': 'Настройки',
  'settings.language': 'Язык',
  'settings.showBothNames': 'Показывать английское название под русским',
  'settings.scanlines': 'Эффект развёртки',
  'settings.data': 'Данные',
  'settings.export': 'Выгрузить резервную копию',
  'settings.import': 'Загрузить резервную копию',
  'settings.importMode': 'Режим загрузки',
  'settings.importReplace': 'Заменить всё',
  'settings.importMerge': 'Объединить с текущим',
  'settings.importDone': 'Резервная копия загружена.',
  'settings.importFailed': 'Это не резервная копия Мяу-трекера.',
  'settings.resetSet': 'Сбросить этот список',
  'settings.resetConfirm': 'Очистить весь прогресс по «{name}»?',
  'settings.prune': 'Удалить прогресс по исчезнувшим предметам',
  'settings.storageNote':
    'Прогресс хранится только в этом браузере. Выгрузите резервную копию, чтобы перенести его.',

  'item.untranslated': 'Русское название не найдено — показано английское.',
  'item.reviewNeeded': 'Автоматический перевод, возможно требует проверки.',

  'rarity.guaranteed': 'Гарантированно',
  'rarity.common': 'Обычное',
  'rarity.rare': 'Редкое',
  'rarity.ultra-rare': 'Очень редкое',
  'rarity.legendary': 'Легендарное',
  'rarity.unknown': 'Неизвестно',

  'type.plan': 'Схема',
  'type.weapon': 'Оружие',
  'type.armor': 'Броня',
  'type.apparel': 'Одежда',
  'type.cosmetic': 'Косметика',
  'type.consumable': 'Расходник',
  'type.mod': 'Мод',
  'type.title': 'Титул',
  'type.journal': 'Записка',
  'type.misc': 'Разное',
  'type.unknown': 'Прочее',

  'footer.sources': 'Источники',
  'footer.ruesoTitle': 'Русские названия предметов',

  'sync.title': 'Синхронизация между устройствами',
  'sync.offNote':
    'Прогресс хранится только в этом браузере. Включите синхронизацию, чтобы видеть тот же список на телефоне.',
  'sync.turnOn': 'Включить синхронизацию',
  'sync.haveCode': 'У меня есть код',
  'sync.enterPrompt': 'Вставьте код с другого устройства',
  'sync.join': 'Подключить',
  'sync.codeLabel': 'Ваш код синхронизации',
  'sync.secretNote':
    'Любой, у кого есть этот код, может читать и менять ваш список. Он же является ключом — без него облачную копию не прочитает никто, включая этот сайт.',
  'sync.showQr': 'Показать QR',
  'sync.hideQr': 'Скрыть QR',
  'sync.qrAlt': 'QR-код, открывающий трекер с уже подключённой синхронизацией',
  'sync.scanNote': 'Наведите камеру телефона — трекер откроется уже подключённым.',
  'sync.copyLink': 'Скопировать ссылку',
  'sync.copyCode': 'Скопировать код',
  'sync.copied': 'Скопировано в буфер обмена.',
  'sync.copyFailed': 'Не удалось скопировать — выделите код и скопируйте вручную.',
  'sync.stop': 'Отключить на этом устройстве',
  'sync.stopErase': 'Отключить и стереть облачную копию',
  'sync.stopConfirm': 'Отключить синхронизацию на этом устройстве? Прогресс здесь останется.',
  'sync.eraseConfirm':
    'Стереть облачную копию? Другие устройства перестанут получать обновления, но сохранят то, что уже загрузили.',
  'sync.statusSyncing': 'Синхронизация…',
  'sync.statusIdle': 'Синхронизировано в {time}',
  'sync.statusWaiting': 'Ожидание первой синхронизации…',
  'sync.fault.network': 'Нет сети — повторим автоматически.',
  'sync.fault.code': 'Этот код не подходит к сохранённым по нему данным.',
  'sync.fault.schema': 'Облачная копия из более новой версии. Сначала обновите это устройство.',
  'sync.fault.server': 'Служба синхронизации не отвечает.',
  'sync.badCode': 'Это не похоже на код синхронизации.',
  'sync.paired': 'Подключено — списки объединены.',
  'sync.pairFailed': 'Не удалось подключиться по этому коду.',
  'sync.limits':
    'Отметки объединяются по одной, поэтому устройства не затирают друг друга. Удаление профиля не переносится — сделайте это на каждом устройстве.',

  'common.cancel': 'Отмена',
  'common.save': 'Сохранить',
  'common.close': 'Закрыть',
  'common.source': 'Источник',
  'common.chance': 'Шанс',
};
